import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import axios from "axios";
import {
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  parsePhoneNumber,
  validatePhoneNumberLength,
} from "libphonenumber-js";
import { ErrorHandlingTypeEnum } from "../enums";
import { ParsedError } from "../interfaces";

export const formatFileSize = (bytes: number, decimalPoint?: number) => {
  if (bytes == 0) return "0 Bytes";
  var k = 1000,
    dm = decimalPoint || 2,
    sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"],
    i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

export const getShortenName = (name?: string) => {
  if (name && name.length > 0) {
    let shortName = name.split(" ")[0];
    if (shortName.length > 20) return shortName.substring(0, 20);
    else return shortName;
  } else {
    return "";
  }
};

export const generateUniqueId = () => {
  return `id${Math.random().toString(16).slice(2)}`;
};

export const secondsToHHMMSS = (totalSeconds: number) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [
    hours.toString().padStart(2, "0"),
    minutes.toString().padStart(2, "0"),
    seconds.toString().padStart(2, "0"),
  ].join(":");
};

/**
 * validate and parse phone number
 * @param countryCode
 * @param phoneNumber
 * @param countryShortCode
 * @returns
 */
export const validatePhoneNumber = (
  countryCode: string,
  phoneNumber: string,
  countryShortCode: string,
): boolean => {
  if (!isPossiblePhoneNumber(phoneNumber, countryShortCode as any)) {
    return false;
  }
  if (!isValidPhoneNumber(phoneNumber, countryShortCode as any)) {
    return false;
  }
  const lengthVerification = validatePhoneNumberLength(
    phoneNumber,
    countryShortCode as any,
  );
  if (
    lengthVerification === "TOO_LONG" ||
    lengthVerification === "NOT_A_NUMBER" ||
    lengthVerification === "INVALID_COUNTRY" ||
    lengthVerification === "INVALID_LENGTH" ||
    lengthVerification === "TOO_SHORT"
  ) {
    return false;
  }
  return true;
};

export const getChartColorsArray = (colors: any) => {
  colors = JSON.parse(colors);
  return colors.map(function (value: any) {
    var newValue = value.replace(" ", "");
    if (newValue.indexOf(",") === -1) {
      var color = getComputedStyle(document.documentElement).getPropertyValue(
        newValue,
      );

      if (color.indexOf("#") !== -1) color = color.replace(" ", "");
      if (color) return color;
      else return newValue;
    } else {
      var val = value.split(",");
      if (val.length === 2) {
        var rgbaColor = getComputedStyle(
          document.documentElement,
        ).getPropertyValue(val[0]);
        rgbaColor = "rgba(" + rgbaColor + "," + val[1] + ")";
        return rgbaColor;
      } else {
        return newValue;
      }
    }
  });
};

export const getStartAndEndOfCurrentMonth = (): {
  startOfMonth: Date;
  endOfMonth: Date;
} => {
  const now = new Date();

  const startOfMonth = new Date(
    now.getFullYear(),
    now.getMonth(),
    1,
    0,
    0,
    0,
    0,
  );

  const endOfMonth = new Date(
    now.getFullYear(),
    now.getMonth() + 1,
    0,
    23,
    59,
    59,
    999,
  );

  return { startOfMonth, endOfMonth };
};

export const getLoggedInUserSession = () => {
  const user = localStorage.getItem("session");
  if (!user) {
    return null;
  } else {
    return JSON.parse(user);
  }
};

export const removeLoggedInUserSession = () => {
  localStorage.removeItem("session");
};

export const flattenErrorMessage = (message: unknown): string => {
  if (message == null || message === "") {
    return "";
  }
  if (typeof message === "string") {
    return message;
  }
  if (typeof message === "number" || typeof message === "boolean") {
    return String(message);
  }
  if (Array.isArray(message)) {
    return message.map(flattenErrorMessage).filter(Boolean).join(", ");
  }
  if (typeof message === "object") {
    return Object.values(message as Record<string, unknown>)
      .map(flattenErrorMessage)
      .filter(Boolean)
      .join(", ");
  }
  return "";
};

const isFieldErrorMap = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const getServerError = (
  error: FetchBaseQueryError | SerializedError | undefined,
): ParsedError => {
  const err = error as FetchBaseQueryError;

  if (err?.data) {
    const errorData: any = err.data;

    if (errorData?.response && errorData?.response.errors) {
      return {
        type: ErrorHandlingTypeEnum.FORM,
        error: errorData.response.errors,
      };
    }

    if (isFieldErrorMap(errorData?.errors)) {
      return {
        type: ErrorHandlingTypeEnum.FORM,
        error: errorData.errors,
      };
    }

    // Field-keyed objects belong on form inputs, not toasts
    if (isFieldErrorMap(errorData?.message)) {
      return {
        type: ErrorHandlingTypeEnum.FORM,
        error: errorData.message,
      };
    }

    // MESSAGE ERRORS (string or array of strings)
    if (errorData?.message) {
      const msg = flattenErrorMessage(errorData.message);

      if (msg.toLowerCase() === "unauthorized") {
        return {
          type: ErrorHandlingTypeEnum.UNKNOWN,
          error: msg,
        };
      }

      return {
        type: ErrorHandlingTypeEnum.ALERT,
        error: msg || "An unexpected error occurred",
      };
    }
  }

  return {
    type: ErrorHandlingTypeEnum.UNKNOWN,
    error: "An unexpected error occurred",
  };
};

const buildTouchedFromErrors = (
  errors: Record<string, unknown>,
): Record<string, unknown> => {
  const touched: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(errors)) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      touched[key] = buildTouchedFromErrors(value as Record<string, unknown>);
    } else if (value) {
      touched[key] = true;
    }
  }

  return touched;
};

export const applyServerFormErrors = (
  form: {
    setErrors: (errors: Record<string, unknown>) => void;
    setTouched: (
      touched: Record<string, unknown>,
      shouldValidate?: boolean,
    ) => void;
  },
  errors: Record<string, unknown>,
) => {
  form.setErrors(errors);
  form.setTouched(buildTouchedFromErrors(errors), false);
};
